import React, {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from 'react'
import { Extension } from '@tiptap/core'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import TextAlignExtension from '../../extensions/textAlignExtension'
import { buildTipTapDocument } from '../../utils/tiptapContent'
import s from './EssayRichText.module.scss'

const anchorHighlightKey = new PluginKey('essay-anchor-highlight')

const AnchorHighlight = Extension.create({
	name: 'essayAnchorHighlight',

	addCommands() {
		return {
			setEssayAnchors:
				anchors =>
				({ tr, dispatch }) => {
					if (!dispatch) return true
					dispatch(
						tr.setMeta(anchorHighlightKey, {
							type: 'setAnchors',
							anchors,
						}),
					)
					return true
				},

			setActiveAnchor:
				anchor =>
				({ tr, dispatch }) => {
					if (!dispatch) return true
					dispatch(
						tr.setMeta(anchorHighlightKey, {
							type: 'setActive',
							anchor,
						}),
					)
					return true
				},

			clearActiveAnchor:
				() =>
				({ tr, dispatch }) => {
					if (!dispatch) return true
					dispatch(
						tr.setMeta(anchorHighlightKey, {
							type: 'clearActive',
						}),
					)
					return true
				},
		}
	},

	addProseMirrorPlugins() {
		return [
			new Plugin({
				key: anchorHighlightKey,

				state: {
					init: () => ({
						anchors: [],
						active: null,
						decorations: DecorationSet.empty,
					}),

					apply(tr, prev) {
						let next = { ...prev }
						const meta = tr.getMeta(anchorHighlightKey)

						if (meta?.type === 'setAnchors') {
							next.anchors = meta.anchors || []
						}

						if (meta?.type === 'setActive') {
							next.active = meta.anchor || null
						}

						if (meta?.type === 'clearActive') {
							next.active = null
						}

						const decos = []

						const createDeco = (a, isActive = false) => {
							if (!Number.isFinite(a.from) || !Number.isFinite(a.to)) return
							if (a.from >= a.to) return

							decos.push(
								Decoration.inline(a.from, a.to, {
									class: isActive
										? 'anchor-highlight-bright'
										: 'anchor-highlight',
								}),
							)

							decos.push(
								Decoration.widget(a.to, () => {
									const btn = document.createElement('button')
									btn.className = s['anchor-feedback-btn']
									btn.innerHTML = '💬'

									btn.onclick = e => {
										e.stopPropagation()

										btn.dispatchEvent(
											new CustomEvent('anchor-click', {
												detail: a,
												bubbles: true,
											}),
										)
									}

									return btn
								}),
							)
						}

						next.anchors.forEach(a => {
							const isActive =
								next.active &&
								a.from === next.active.from &&
								a.to === next.active.to

							createDeco(a, isActive)
						})

						next.decorations = DecorationSet.create(tr.doc, decos)

						return next
					},
				},

				props: {
					decorations(state) {
						return this.getState(state)?.decorations
					},

					handleClick(view, pos) {
						const state = anchorHighlightKey.getState(view.state)
						if (!state?.anchors?.length) return false

						const clicked = state.anchors.find(
							a => pos >= a.from && pos <= a.to,
						)

						if (!clicked) return false

						view.dom.dispatchEvent(
							new CustomEvent('anchor-click', {
								detail: clicked,
								bubbles: true,
							}),
						)

						return true
					},
				},
			}),
		]
	},
})

const EssayRichText = forwardRef(function EssayRichText(
	{
		versionId,
		contentJson,
		contentText,
		isEditing = false,
		className,
		onTextSelected,
		selectionDisabled = false,
		onAnchorClick,
	},
	ref,
) {
	const initial = buildTipTapDocument(contentJson, contentText)

	const rootRef = useRef(null)
	const clearOnCollapsedRef = useRef(false)
	const isProgrammaticSelectionRef = useRef(false)

	const [, setSelectionActionPos] = useState(null)

	const editor = useEditor({
		extensions: [
			StarterKit,
			Link.configure({ openOnClick: false }),
			TextAlignExtension.configure({
				types: ['heading', 'paragraph'],
			}),
			AnchorHighlight,
		],
		content: initial,
		editable: isEditing,
	})

	useEffect(() => {
		if (!editor) return
		editor.setEditable(isEditing)
	}, [editor, isEditing])

	useEffect(() => {
		const el = rootRef.current
		if (!el) return

		const handler = e => {
			onAnchorClick?.(e.detail)
		}

		el.addEventListener('anchor-click', handler)

		return () => {
			el.removeEventListener('anchor-click', handler)
		}
	}, [onAnchorClick])

	useEffect(() => {
		if (!editor || isEditing) return
		const doc = buildTipTapDocument(contentJson, contentText)
		editor.commands.setContent(doc)
	}, [editor, versionId, contentJson, contentText, isEditing])

	useEffect(() => {
		if (!editor) return

		const emitSelection = () => {
			if (isProgrammaticSelectionRef.current) {
				isProgrammaticSelectionRef.current = false
				return
			}

			const { from, to } = editor.state.selection || {}

			if (!Number.isFinite(from) || !Number.isFinite(to) || from === to) {
				setSelectionActionPos(null)

				if (clearOnCollapsedRef.current) {
					onTextSelected?.(null)
				}

				return
			}

			if (selectionDisabled) {
				setSelectionActionPos(null)
				clearOnCollapsedRef.current = false
				return
			}

			const text = editor.state.doc.textBetween(from, to, ' ', ' ').trim()

			if (!text) {
				setSelectionActionPos(null)
				onTextSelected?.(null)
				return
			}

			const rootRect = rootRef.current?.getBoundingClientRect?.()
			const endPos = editor.view.coordsAtPos(to)

			setSelectionActionPos(
				rootRect
					? {
							top: endPos.bottom - rootRect.top + 8,
							left: endPos.right - rootRect.left,
						}
					: null,
			)

			onTextSelected?.({
				text,
				from,
				to,
				contextBefore: editor.state.doc
					.textBetween(Math.max(1, from - 30), from, ' ', ' ')
					.trim(),
				contextAfter: editor.state.doc
					.textBetween(
						to,
						Math.min(editor.state.doc.content.size, to + 30),
						' ',
						' ',
					)
					.trim(),
			})

			clearOnCollapsedRef.current = false
		}

		editor.on('selectionUpdate', emitSelection)

		return () => {
			editor.off('selectionUpdate', emitSelection)
		}
	}, [editor, onTextSelected, selectionDisabled])

	useImperativeHandle(
		ref,
		() => ({
			isReady: () => !!editor,

			getSnapshot: () => {
				if (!editor) return null
				return {
					contentJson: editor.getJSON(),
					contentHtml: editor.getHTML(),
					contentText: editor.getText(),
					wordCount: editor.getText().trim().split(/\s+/).filter(Boolean).length,
				}
			},

			setAnchors: anchors => {
				if (!editor) return
				editor.commands.setEssayAnchors(anchors)
			},

			setActiveAnchor: anchor => {
				if (!editor) return
				isProgrammaticSelectionRef.current = true
				editor.commands.setActiveAnchor(anchor)
			},

			clearActiveAnchor: () => {
				if (!editor) return
				editor.commands.clearActiveAnchor()
			},

			focusAnchorText: targetText => {
				if (!editor) return false

				const needle = String(targetText || '').trim()
				if (!needle) return false

				let foundFrom = null
				let foundTo = null

				editor.state.doc.descendants((node, pos) => {
					if (!node.isText) return
					const text = node.text || ''
					const idx = text.indexOf(needle)
					if (idx !== -1 && foundFrom == null) {
						foundFrom = pos + idx
						foundTo = foundFrom + needle.length
					}
				})

				if (foundFrom == null) return false

				isProgrammaticSelectionRef.current = true

				editor.commands.setTextSelection({
					from: foundFrom,
					to: foundTo,
				})

				editor.commands.setActiveAnchor({
					from: foundFrom,
					to: foundTo,
				})

				return true
			},
		}),
		[editor],
	)

	if (!editor) return <div className={className} />

	return (
		<div
			ref={rootRef}
			onMouseDownCapture={() => {
				clearOnCollapsedRef.current = true
			}}
			className={`${s.root} ${className || ''} ${
				isEditing ? s.rootEditing : ''
			}`}
		>
			<EditorContent editor={editor} className={s.editor} />
		</div>
	)
})

export default EssayRichText
